import { useEffect, useMemo, useState } from 'react';
import { CustomUser, Firm, UserPermissionWithPath } from 'api';
import { firmsAction } from 'common/actions';
import { FIRM_LIST_STATUSES, FirmListStatus } from 'common/types/firm';
import { toBooleanFlag } from 'pages/Auth/utils';
import UsersService from 'services/users';

export interface AuthRequirementsState {
  error: string | null;
  firmList: Firm[];
  firmListStatus: FirmListStatus;
  isLoading: boolean;
  lowerPermissions: UserPermissionWithPath[];
  requiresTwoFactor: boolean;
  userHasTwoFactor: boolean;
}

const usersService = new UsersService();

const initialState: AuthRequirementsState = {
  error: null,
  firmList: [],
  firmListStatus: FIRM_LIST_STATUSES.IDLE,
  isLoading: false,
  lowerPermissions: [],
  requiresTwoFactor: false,
  userHasTwoFactor: false,
};

const useAuthRequirements = (user?: CustomUser | null) => {
  const [state, setState] = useState<AuthRequirementsState>(initialState);

  useEffect(() => {
    let isMounted = true;

    const loadRequirements = async () => {
      if (!user?.id) {
        setState(initialState);
        return;
      }

      setState(currentState => ({ ...currentState, error: null, isLoading: true }));

      try {
        // Product/workspace routing needs both firm access and lower-level object permissions.
        const [firmListResponse, lowerPermissions] = await Promise.all([
          firmsAction.getFirmList(),
          usersService.getLowerUserPermissions(user.id),
        ]);
        const firmList = Array.isArray(firmListResponse?.firms) ? firmListResponse.firms : [];
        const firmListStatus = Array.isArray(firmListResponse?.firms)
          ? FIRM_LIST_STATUSES.READY
          : FIRM_LIST_STATUSES.FORBIDDEN;

        if (!isMounted) {
          return;
        }

        setState({
          error: null,
          firmList,
          firmListStatus,
          isLoading: false,
          lowerPermissions: lowerPermissions ?? [],
          requiresTwoFactor: toBooleanFlag(firmListResponse?.requires_2fa),
          userHasTwoFactor: toBooleanFlag(firmListResponse?.user_has_2fa) || toBooleanFlag(user?.has_2fa),
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setState(currentState => ({
          ...currentState,
          error: error instanceof Error ? error.message : 'Unable to load authentication requirements.',
          firmListStatus: FIRM_LIST_STATUSES.IDLE,
          isLoading: false,
        }));
      }
    };

    loadRequirements();

    return () => {
      isMounted = false;
    };
  }, [user?.has_2fa, user?.id]);

  return useMemo(() => state, [state]);
};

export default useAuthRequirements;
